import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import { mapplsGeocode } from '../../../utils/geocoding';
import { AuthContext } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const MedicalTestBooking = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { cart, removeItem, clearCart } = useCart();
  
  // Load selected items from localStorage
  const [selectedTests, setSelectedTests] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    date: '',
    time: '',
    latitude: '',
    longitude: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // New states for delivery and pricing
  const [deliveryInfo, setDeliveryInfo] = useState({
    distance: null,
    deliveryCharge: 0,
    isServiceable: false
  });
  const [priceBreakdown, setPriceBreakdown] = useState({
    subtotal: 0,
    deliveryCharge: 0,
    total: 0
  });

  useEffect(() => {
    // Load selected items from localStorage
    const storedTests = localStorage.getItem('selectedTests');
    const storedPackages = localStorage.getItem('selectedPackages');
    
    if (storedTests) {
      try {
        setSelectedTests(JSON.parse(storedTests));
      } catch (err) {
        console.error('Error parsing stored tests:', err);
        setSelectedTests([]);
      }
    }
    
    if (storedPackages) {
      try {
        setSelectedPackages(JSON.parse(storedPackages));
      } catch (err) {
        console.error('Error parsing stored packages:', err);
        setSelectedPackages([]);
      }
    }

    // Fetch addresses if user is logged in
    if (token) {
      fetchAddresses();
    }
  }, [token]);

  // Calculate prices when tests/packages change
  useEffect(() => {
    calculatePriceBreakdown();
  }, [selectedTests, selectedPackages, deliveryInfo.deliveryCharge]);

  // Check delivery availability when location changes
  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      checkDeliveryAvailability();
    }
  }, [formData.latitude, formData.longitude]);

  const fetchAddresses = async () => {
    try {
      const response = await api.get('/addresses/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(response.data.addresses || response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching addresses:', err);
      setAddresses([]);
    }
  };

  const checkDeliveryAvailability = async () => {
    try {
      const response = await api.post('/medical-tests/check-delivery/', {
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeliveryInfo({
        distance: response.data.distance_km,
        deliveryCharge: response.data.delivery_charge,
        isServiceable: response.data.available !== undefined ? response.data.available : response.data.is_serviceable
      });
    } catch (err) {
      console.error('Error checking delivery:', err);
      setDeliveryInfo({
        distance: null,
        deliveryCharge: 0,
        isServiceable: false
      });
    }
  };

  const calculatePriceBreakdown = () => {
    const testsTotal = selectedTests.reduce((total, test) => {
      return total + (parseFloat(test.offer_price) || parseFloat(test.price) || 0);
    }, 0);
    
    const packagesTotal = selectedPackages.reduce((total, pkg) => {
      return total + (parseFloat(pkg.offer_price) || parseFloat(pkg.total_price) || 0);
    }, 0);
    
    const subtotal = testsTotal + packagesTotal;
    
    setPriceBreakdown({
      subtotal,
      deliveryCharge: deliveryInfo.deliveryCharge,
      total: subtotal + deliveryInfo.deliveryCharge
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressSelect = async (address) => {
    setSelectedAddress(address);
    let lat = address.latitude?.toString() || '';
    let lng = address.longitude?.toString() || '';

    // If backend stored null lat/lng, geocode using city + state + pincode
    if (!lat || !lng) {
      try {
        const geocodeQuery = [address.city, address.state, address.pincode, 'India'].filter(Boolean).join(', ');
        const coords = await mapplsGeocode(geocodeQuery);
        if (coords) {
          lat = coords.lat.toString();
          lng = coords.lng.toString();
        }
      } catch {
        // coords stay empty; user can still use GPS or type manually
      }
    }

    setFormData(prev => ({
      ...prev,
      address: `${address.address}, ${address.city}, ${address.state} - ${address.pincode}`,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleAddNewAddress = () => {
    alert('Navigate to Add Address page: /user/addresses/add');
  };

  const handleRemoveTest = async (testId) => {
    try {
      // Find the cart item for this test
      const cartItem = cart?.test_items?.find(item => item.test?.id === testId);

      if (cartItem) {
        // Remove from cart via API
        await removeItem('test', cartItem.id);
      }

      // Also update local state
      const updatedTests = selectedTests.filter(test => test.id !== testId);
      setSelectedTests(updatedTests);
      localStorage.setItem('selectedTests', JSON.stringify(updatedTests));
    } catch (error) {
      console.error('Error removing test:', error);
      alert(error.message || 'Failed to remove test from cart');
    }
  };

  const handleRemovePackage = async (packageId) => {
    try {
      // Find the cart item for this package
      const cartItem = cart?.test_items?.find(item => item.package?.id === packageId);

      if (cartItem) {
        // Remove from cart via API
        await removeItem('test', cartItem.id);
      }

      // Also update local state
      const updatedPackages = selectedPackages.filter(pkg => pkg.id !== packageId);
      setSelectedPackages(updatedPackages);
      localStorage.setItem('selectedPackages', JSON.stringify(updatedPackages));
    } catch (error) {
      console.error('Error removing package:', error);
      alert(error.message || 'Failed to remove package from cart');
    }
  };

  const handleClearAllTests = async () => {
    try {
      // Remove all tests from cart via API
      if (cart?.test_items) {
        const testItems = cart.test_items.filter(item => item.test);
        for (const item of testItems) {
          await removeItem('test', item.id);
        }
      }

      // Also update local state
      setSelectedTests([]);
      localStorage.setItem('selectedTests', JSON.stringify([]));
    } catch (error) {
      console.error('Error clearing tests:', error);
      alert(error.message || 'Failed to clear tests from cart');
    }
  };

  const handleClearAllPackages = async () => {
    try {
      // Remove all packages from cart via API
      if (cart?.test_items) {
        const packageItems = cart.test_items.filter(item => item.package);
        for (const item of packageItems) {
          await removeItem('test', item.id);
        }
      }

      // Also update local state
      setSelectedPackages([]);
      localStorage.setItem('selectedPackages', JSON.stringify([]));
    } catch (error) {
      console.error('Error clearing packages:', error);
      alert(error.message || 'Failed to clear packages from cart');
    }
  };

  const handleAddMoreTests = () => {
    navigate('/medical-tests');
  };

  const getCurrentLocation = () => {
    setLocationLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
        setLocationLoading(false);
      },
      () => {
        setError('Unable to get your location. Please use the address option instead.');
        setLocationLoading(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // mapplsGeocode imported from src/utils/geocoding.js

  const geocodeAddress = async () => {
    const address = formData.address.trim();
    if (!address) {
      setError('Please enter an address above before checking delivery.');
      return;
    }
    setGeocoding(true);
    setError(null);
    try {
      const found = await mapplsGeocode(address);
      if (!found) {
        setError('Location not found. Try the GPS button, or enter a more specific address.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        latitude: found.lat,
        longitude: found.lng,
      }));
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Could not look up the address. Check your connection and try again.');
    } finally {
      setGeocoding(false);
    }
  };

  const initiatePayment = async () => {
    try {
      setPaymentLoading(true);
      setError(null);

      // Step 1: Book the appointment to get an appointment_id
      const bookingPayload = {
        test_ids: selectedTests.map(test => test.id),
        date: formData.date,
        time: formData.time,
        address: formData.address,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
      };

      const bookingResponse = await api.post('/medical-tests/book/', bookingPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!bookingResponse.data.success) {
        throw new Error(bookingResponse.data.message || 'Booking failed');
      }

      const appointmentId = bookingResponse.data.data.id;

      // Step 2: Create Razorpay order — server calculates amount from service_id
      const orderResponse = await api.post('/payment/create-order/', {
        service_type: 'medical_test',
        service_id: appointmentId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { razorpay_order_id, key, amount } = orderResponse.data;

      // Step 3: Ensure Razorpay SDK is loaded, then open modal
      if (typeof window.Razorpay === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load payment gateway. Please refresh and try again.'));
          document.body.appendChild(script);
        });
      }
      const options = {
        key,
        amount,
        currency: 'INR',
        name: 'HomeCure',
        description: 'Medical Test Booking Payment',
        order_id: razorpay_order_id,
        handler: async function (response) {
          try {
            // Step 4: Poll GET /payment/status/<razorpay_order_id>/ — backend confirms via webhook
            const maxAttempts = 6;
            let confirmed = false;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              const statusRes = await api.get(
                `/payment/status/${response.razorpay_order_id}/`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              if (statusRes.data.status === 'captured') {
                confirmed = true;
                break;
              }
              if (statusRes.data.status === 'failed') {
                throw new Error('Payment failed on server side.');
              }
              if (attempt < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }

            if (confirmed) {
              localStorage.removeItem('selectedTests');
              localStorage.removeItem('selectedPackages');
              setSelectedTests([]);
              setSelectedPackages([]);
              setBookingSuccess(true);
              setFormData({ address: '', date: '', time: '', latitude: '', longitude: '' });
              setSelectedAddress(null);
            } else {
              setError('Payment is being verified. If money was deducted, contact support.');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            setError(err.message || 'Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user?.name || user?.first_name || '',
          email: user?.email || '',
          contact: user?.mobile || user?.phone || '',
        },
        theme: { color: '#3B82F6' },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false);
            setError('Payment cancelled by user');
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error('Payment initiation error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user && !token) {
      setError('Please log in to book an appointment');
      return;
    }

    if (selectedTests.length === 0 && selectedPackages.length === 0) {
      setError('Please select at least one test or package');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      setError('Location coordinates are required');
      return;
    }

    if (!deliveryInfo.isServiceable) {
      setError('Service not available at this location');
      return;
    }

    // Initiate payment process
    await initiatePayment();
  };

  const handleBackToTests = () => {
    navigate('/medical-tests');
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Success message component
  if (bookingSuccess) {
    return (
      
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-green-900 mb-2">Booking Confirmed!</h2>
          <p className="text-green-700 mb-6">
            Your medical test appointment has been successfully booked and payment confirmed.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">📱</div>
              <h3 className="font-semibold text-gray-900">SMS Confirmation</h3>
              <p className="text-sm text-gray-600">Sent to your mobile</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">🏠</div>
              <h3 className="font-semibold text-gray-900">Home Collection</h3>
              <p className="text-sm text-gray-600">Team will visit as scheduled</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">📧</div>
              <h3 className="font-semibold text-gray-900">Email Invoice</h3>
              <p className="text-sm text-gray-600">Invoice sent to your email</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setBookingSuccess(false);
              navigate('/medical-tests');
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Book Another Test
          </button>
        </div>
      </div>
    );
  }

  // If no items selected, show empty cart message
  if (selectedTests.length === 0 && selectedPackages.length === 0) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              {/* Empty Cart Icon */}
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.8 7.2M7 13l2.5-5m6.5 5l2.5-5M9 19.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM20 19.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                </svg>
              </div>

              {/* Message */}
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Your Cart is Empty</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Start adding medical tests or health packages to book your home sample collection
              </p>

              {/* Action Button */}
              <button
                onClick={handleBackToTests}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Browse Medical Tests
              </button>

              {/* Features */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Free Collection</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>24-48h Reports</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>NABL Certified</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Digital Reports</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
        <Header />
    <div className="max-w-4xl mx-auto p-6">
      {/* Back Button */}
      <button
        onClick={handleBackToTests}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors mb-6"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Medical Tests
      </button>

      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your Cart & Booking
          </h2>
          <p className="text-gray-600">Review items and schedule your home collection</p>
        </div>
        <button
          onClick={handleAddMoreTests}
          className="flex items-center text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors font-medium"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add More Tests
        </button>
      </div>
      
      {/* Cart Items Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">
            Cart Items ({selectedTests.length + selectedPackages.length})
          </h3>
          <div className="flex gap-2">
            {selectedTests.length > 0 && (
              <button
                onClick={handleClearAllTests}
                className="text-red-600 hover:text-red-700 font-medium text-sm bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
              >
                Clear Tests
              </button>
            )}
            {selectedPackages.length > 0 && (
              <button
                onClick={handleClearAllPackages}
                className="text-red-600 hover:text-red-700 font-medium text-sm bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
              >
                Clear Packages
              </button>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Individual Tests */}
          {selectedTests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Individual Tests:</h4>
              <div className="space-y-2">
                {selectedTests.map((test, index) => (
                  <div key={test.id || index} className="flex justify-between items-center bg-white rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <span className="text-gray-900 font-medium">{test.title}</span>
                        {test.offer_price && test.offer_price !== test.price && (
                          <span className="text-xs text-green-600 block">
                            Save ₹{test.price - test.offer_price}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        {test.offer_price && test.offer_price !== test.price ? (
                          <div>
                            <span className="font-bold">₹{test.offer_price}</span>
                            <div className="text-xs text-gray-500 line-through">₹{test.price}</div>
                          </div>
                        ) : (
                          <span className="font-bold">₹{test.price}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveTest(test.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test Packages */}
          {selectedPackages.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Test Packages:</h4>
              <div className="space-y-2">
                {selectedPackages.map((pkg, index) => (
                  <div key={pkg.id || index} className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">{pkg.name}</span>
                          <div className="text-xs text-gray-600">
                            {pkg.test_count || pkg.tests?.length || 0} tests included
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          {pkg.offer_price && pkg.offer_price !== pkg.total_price ? (
                            <div>
                              <span className="font-bold">₹{pkg.offer_price}</span>
                              <div className="text-xs text-gray-500 line-through">₹{pkg.total_price}</div>
                            </div>
                          ) : (
                            <span className="font-bold">₹{pkg.total_price}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemovePackage(pkg.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Package Tests */}
                    {pkg.tests && pkg.tests.length > 0 && (
                      <div className="bg-gray-50 rounded p-2 mt-2">
                        <p className="text-xs text-gray-600 mb-1">Includes:</p>
                        <div className="flex flex-wrap gap-1">
                          {pkg.tests.map((test, testIndex) => (
                            <span key={testIndex} className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                              {test.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Price Breakdown */}
        <div className="border-t border-blue-200 pt-4 mt-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{priceBreakdown.subtotal}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery Charge:</span>
              <span>₹{priceBreakdown.deliveryCharge}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>₹{priceBreakdown.total}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Booking Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Address Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-lg font-semibold text-gray-900">
                Collection Address *
              </label>
              <button
                type="button"
                onClick={handleAddNewAddress}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Add New Address
              </button>
            </div>

            {/* Saved Addresses */}
            {addresses.length > 0 && (
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Choose from saved addresses:
                </label>
                {addresses.map((address) => (
                  <div 
                    key={address.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedAddress?.id === address.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => handleAddressSelect(address)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          selectedAddress?.id === address.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedAddress?.id === address.id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{address.label || 'Address'}</span>
                            {address.is_default && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm">
                            {address.address}, {address.city}, {address.state} - {address.pincode}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Manual Address Entry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or enter custom address:
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your complete address for home collection"
              />
            </div>
          </div>

          {/* Date & Time Section */}
          <div>
            <label className="text-lg font-semibold text-gray-900 mb-4 block">
              Preferred Date & Time *
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  min={getMinDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time
                </label>
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select time slot</option>
                  <option value="08:00">8:00 AM - 10:00 AM</option>
                  <option value="10:00">10:00 AM - 12:00 PM</option>
                  <option value="14:00">2:00 PM - 4:00 PM</option>
                  <option value="16:00">4:00 PM - 6:00 PM</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div>
            <label className="text-lg font-semibold text-gray-900 mb-3 block">
              Confirm Collection Location *
            </label>
            <p className="text-sm text-gray-500 mb-4">
              We need your location to check home collection availability and calculate delivery charges.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* Geocode address button */}
              <button
                type="button"
                onClick={geocodeAddress}
                disabled={geocoding || locationLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
              >
                {geocoding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Looking up address...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Check Delivery for Entered Address
                  </>
                )}
              </button>

              {/* GPS button */}
              <button
                type="button"
                onClick={getCurrentLocation}
                disabled={locationLoading || geocoding}
                className="flex-1 flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 disabled:bg-gray-400 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
              >
                {locationLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Detecting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Use My Current Location
                  </>
                )}
              </button>
            </div>

            {/* Delivery Information */}
            {deliveryInfo.distance !== null && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Distance:</span>
                    <span className="font-medium ml-1">{(parseFloat(deliveryInfo.distance) || 0).toFixed(1)} km</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Delivery Charge:</span>
                    <span className="font-medium ml-1">₹{deliveryInfo.deliveryCharge}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Service Status:</span>
                    <span className={`font-medium ml-1 ${
                      deliveryInfo.isServiceable ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {deliveryInfo.isServiceable ? 'Available' : 'Not Available'}
                    </span>
                  </div>
                </div>
                {!deliveryInfo.isServiceable && (
                  <p className="text-red-600 text-sm mt-2">
                    Service not available beyond 30 km radius. Please choose a location within 30 km.
                  </p>
                )}
              </div>
            )}

            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                We calculate delivery charges based on distance from our center
              </p>
            </div>
          </div>

          {/* Submit Section */}
          <div className="bg-gray-50 -m-6 mt-6 p-6 border-t">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-semibold text-gray-900">Total Amount</p>
                <p className="text-sm text-gray-600">Inclusive of all taxes and delivery charges</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  ₹{priceBreakdown.total}
                </p>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading || paymentLoading || !deliveryInfo.isServiceable || (selectedTests.length === 0 && selectedPackages.length === 0)}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors text-lg"
            >
              {loading || paymentLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  {paymentLoading ? 'Processing Payment...' : 'Loading...'}
                </div>
              ) : (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Pay ₹{priceBreakdown.total} & Book Appointment
                </span>
              )}
            </button>

            <div className="mt-3 text-center">
              <p className="text-sm text-gray-600">
                🔒 Secure payment via Razorpay • 📧 Instant confirmation • 🏠 Free home collection
              </p>
            </div>
          </div>
        </form>
      </div>

      {/* Service Information */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">What to Expect</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Safe & Secure</h4>
            <p className="text-sm text-gray-600">NABL accredited standards</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Professional Team</h4>
            <p className="text-sm text-gray-600">Certified technicians</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Fast Results</h4>
            <p className="text-sm text-gray-600">Reports within 24-48 hrs</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Confirmation Call</h4>
            <p className="text-sm text-gray-600">Within 30 minutes</p>
          </div>
        </div>
      </div>

      {/* Add Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
    </div>
      <Footer />
      </>
  );
};

export default MedicalTestBooking;